/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { config } from "../../common/config";
import { Locale } from "../../common/locale";

@Component({
  tag: "gx-ide-wf-settings",
  styleUrl: "wf-settings.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/wf-settings"]
})
export class GxIdeWFSettings {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;
  private renderedFirstTime = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeWfSettingsElement;
  private installGxFlowEl!: HTMLGxgFormCheckboxElement;
  private futureUpgradesEl!: HTMLGxgComboBoxElement;

  // 3.STATE() VARIABLES //

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Indicates the type of configuration being run, the value of this property should be used to determine
   * what long text should be displayed in this dialog.
   */
  @Prop() readonly configurationType: "installation" | "update" =
    "installation";

  /**
   * Defines default config to initialize dialog
   */
  @Prop() readonly defaultConfiguration: WFConfigData | undefined = {
    installClient: true,
    futureUpgrades: "automatic"
  };

  /**
   * Callback invoked when the user presses the 'OK' button. As a parameter, it receives the data entered by the user
   */
  @Prop() readonly saveCallback: (config: WFConfigData) => Promise<void>;

  /**
   * Callback invoked when the user presses the 'Cancel' button.
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this._componentLocale.componentName
      );
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private cancelHandler = () => {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
  };

  private saveCallbackHandler = () => {
    if (this.saveCallback) {
      const wfConfigData: WFConfigData = {
        installClient: this.installGxFlowEl.checked,
        futureUpgrades: this.futureUpgradesEl.value
      };
      this.saveCallback(wfConfigData);
    }
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container slimmerFooter={config.gxIdeContainer.slimmerFooter}>
            <div class="message-item">
              <gxg-icon
                type="gx-server/changes-commit-pending"
                color="auto"
                size="small"
              ></gxg-icon>
              <gxg-text>
                {this._componentLocale.description[this.configurationType]}
              </gxg-text>
            </div>
            <div class="options">
              <gxg-form-checkbox
                label={this._componentLocale.installGxFlow}
                checked={this.defaultConfiguration.installClient}
                ref={el =>
                  (this.installGxFlowEl = el as HTMLGxgFormCheckboxElement)
                }
              ></gxg-form-checkbox>
              <gxg-combo-box
                class="upgrades-options"
                label={this._componentLocale.manageUpgrades}
                disableFilter
                value={this.defaultConfiguration.futureUpgrades}
                ref={el =>
                  (this.futureUpgradesEl = el as HTMLGxgComboBoxElement)
                }
              >
                <gxg-combo-box-item value="automatic">
                  {this._componentLocale.upgradesOptions[0]}
                </gxg-combo-box-item>
                <gxg-combo-box-item value="prompt">
                  {this._componentLocale.upgradesOptions[1]}
                </gxg-combo-box-item>
                <gxg-combo-box-item value="never">
                  {this._componentLocale.upgradesOptions[2]}
                </gxg-combo-box-item>
              </gxg-combo-box>
            </div>
            <gxg-text textAlign="end" type="text-regular">
              {this._componentLocale.prompt[this.configurationType]}
            </gxg-text>
            <gxg-button
              slot="footer-end"
              type="outlined"
              onClick={this.cancelHandler}
            >
              Cancel
            </gxg-button>
            <gxg-button slot="footer-end" onClick={this.saveCallbackHandler}>
              {
                this._componentLocale.footer.confirmButton[
                  this.configurationType
                ]
              }
            </gxg-button>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type WFConfigData = {
  installClient: boolean;
  futureUpgrades: "never" | "prompt" | "automatic";
};

export type saveCallback = (config: WFConfigData) => Promise<void>;
